<template>
  <div class="d-flex flex-column flex-fill">
    <h3>Education</h3>
    <b-card class="mb-4" v-for="(education, i) in educations" :key="`${i}-${education.id}`">
      <b-alert v-if="typeof errors[i] === 'string'" show variant="danger">
        {{errors[i]}}
      </b-alert>
      <b-form-group label-cols="12" label="School Name*">
        <greenhouse-multi-select
          :endpoint="`https://boards-api.greenhouse.io/v1/boards/${jobBoardToken}/education/schools`"
          v-model="education.schoolName"
        />
      </b-form-group>
      <b-form-group label-cols="12" label="Degree*">
        <greenhouse-multi-select
          :endpoint="`https://boards-api.greenhouse.io/v1/boards/${jobBoardToken}/education/degrees`"
          v-model="education.degree"
        />
      </b-form-group>
      <b-form-group label-cols="12" label="Discipline*">
        <greenhouse-multi-select
          :endpoint="`https://boards-api.greenhouse.io/v1/boards/${jobBoardToken}/education/disciplines`"
          v-model="education.discipline"
        />
      </b-form-group>
      <b-form-group label-cols="12" label="Start Date*">
        <div class="d-flex flex-row">
          <b-form-select v-model="education.startMonth" :options="monthsList">
            <template #first>
              <b-form-select-option :value="null" disabled>-- Please select a month --</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-select v-model="education.startYear" :options="yearsListPast">
            <template #first>
              <b-form-select-option :value="null" disabled>-- Please select a year --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </b-form-group>
      <b-form-group label-cols="12" label="End Date*">
        <div class="d-flex flex-row">
          <b-form-select v-model="education.endMonth" :options="monthsList">
            <template #first>
              <b-form-select-option :value="null" disabled>-- Please select a month --</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-select v-model="education.endYear" :options="yearsListPastAndFuture">
            <template #first>
              <b-form-select-option :value="null" disabled>-- Please select a year --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </b-form-group>
      <div class="d-flex flex-row justify-content-end">
        <b-btn v-if="educations.length > 1" variant="outline-danger" @click="() => educations.splice(i, 1)">Remove</b-btn>
      </div>
    </b-card>
    <div class="d-flex flex-row justify-content-end">
      <b-btn variant="outline-primary" @click="createNewEducation">Add New</b-btn>
    </div>
  </div>
</template>

<script>
import CheckboxGroup from '@/components/settings/CheckboxGroup';
import Share from '@src/components/Share';
import GreenhouseMultiSelect from '@src/components/misc/GreenhouseMultiSelect';
import gridMixin from '@src/mixins/grid';
import { tryNull } from '@src/lib/utils';

export default {
  props: {
    jobBoardToken: {
      type: String,
    },
    value: {
      type: Array,
      default: null,
    },
  },
  components: {
    Share,
    CheckboxGroup,
    GreenhouseMultiSelect,
  },
  mixins: [
    gridMixin,
  ],
  data: () => ({
    educations: [],
    errors: [],
  }),
  created() {
    const parsed = tryNull(() => JSON.parse(JSON.stringify(this.value)));
    if (Array.isArray(parsed) && parsed.length) {
      this.educations = parsed;
      this.errors = parsed.map(() => null);
    } else {
      this.createNewEducation();
    }
  },
  computed: {
    monthsList() {
      return [
        { text: 'January', value: 1 },
        { text: 'February', value: 2 },
        { text: 'March', value: 3 },
        { text: 'April', value: 4 },
        { text: 'May', value: 5 },
        { text: 'June', value: 6 },
        { text: 'July', value: 7 },
        { text: 'August', value: 8 },
        { text: 'September', value: 9 },
        { text: 'October', value: 10 },
        { text: 'November', value: 11 },
        { text: 'December', value: 12 },
      ];
    },
    yearsListPast() {
      const currentYear = (new Date()).getFullYear();
      const list = [];

      for (let i = 0; i < 50; i++) {
        list.push(currentYear - i);
      }

      return list;
    },
    yearsListPastAndFuture() {
      const currentYear = (new Date()).getFullYear();
      const list = [];

      for (let i = -10; i < 50; i++) {
        list.push(currentYear - i);
      }

      return list;
    },
  },
  methods: {
    createNewEducation() {
      this.educations.push({
        id: Date.now(),
        schoolName: null,
        degree: null,
        discipline: null,
        startMonth: null,
        startYear: null,
        endMonth: null,
        endYear: null,
      });
      this.errors.push(null);
    },
    educationsMove(up, i) {
      if (i === 0 && up) {
        return false;
      }
      if (i === this.educations.length - 1 && !up) {
        return false;
      }

      const j = up ? i - 1 : i + 1;
      const [ value ] = this.educations.splice(i, 1);
      this.educations.splice(j, 0, value);
    },
    emitEducationInput() {
      this.$emit('input', JSON.parse(JSON.stringify(this.educations.reduce((list, edu, i) => {
        try {
          const clean = this.formatEducation(edu);
          this.errors[i] = clean ? null : 'Failed to format education data';
          return list.concat(clean || []);
        } catch (err) {
          this.errors[i] = err.message;
          return list;
        }
      }, []))));
    },
    formatEducation(input) {
      this.$assert(input.schoolName && input.schoolName.id, 'Missing schoolName choice');
      this.$assert(input.degree && input.degree.id, 'Missing degree choice');
      this.$assert(input.discipline && input.discipline.id, 'Missing discipline choice');
      this.$assert(input.startMonth, 'Missing startMonth choice');
      this.$assert(input.startYear, 'Missing startYear choice');
      this.$assert(input.endMonth, 'Missing endMonth choice');
      this.$assert(input.endYear, 'Missing endYear choice');

      this.$assert(input.startYear <= input.endYear, 'Start date cannot be later than end date');

      return {
        schoolNameId: input.schoolName.id,
        degreeID: input.degree.id,
        disciplineID: input.discipline.id,
        startDate: { month: input.startMonth, year: input.startYear },
        endDate: { month: input.endMonth, year: input.endYear },
      };
    },
  },
  watch: {
    educations: {
      deep: true,
      handler() {
        this.emitEducationInput();
      },
    },
  },
};
</script>

<style lang="scss">
.referral-jam-event-settings {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/jobs/GreenhouseEducationQuestions.vue